import React, { useMemo, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import {
  loginWithEmail,
  loginWithGoogle,
  requestPasswordResetOtp,
  resetPassword,
  setupPassword,
  verifyPasswordResetOtp,
} from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import {
  btnPrimary,
  btnSecondary,
  cardGlass,
  inputField,
  pageContainer,
  pageShell,
  pageTitle,
} from "../../components/ui/saasPrimitives";

const VIEWS = {
  LOGIN: "login",
  SETUP: "setup",
  FORGOT_REQUEST: "forgot_request",
  FORGOT_VERIFY: "forgot_verify",
  FORGOT_RESET: "forgot_reset",
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const [view, setView] = useState(VIEWS.LOGIN);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [resetToken, setResetToken] = useState("");

  const normalizedEmail = useMemo(() => String(email || "").trim().toLowerCase(), [email]);

  const resetMessages = () => {
    setError("");
    setNotice("");
  };

  if (isAuthenticated) {
    return (
      <div className={pageShell}>
        <div className={`${pageContainer} max-w-lg`}>
          <div className={`${cardGlass} text-center ring-1 ring-slate-100`}>
            <h1 className={pageTitle}>Already logged in</h1>
            <p className="text-slate-600">{user?.email}</p>
            <button type="button" className={`${btnPrimary} mt-6`} onClick={() => navigate("/admin/dashboard")}>
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const applyLogin = (data) => {
    if (!data?.token || !data?.user) {
      throw new Error("Invalid login response");
    }
    login(data.token, data.user);
    navigate("/admin/dashboard");
  };

  const onGoogleSuccess = async (credentialResponse) => {
    if (loading) return;
    try {
      resetMessages();
      const credential = credentialResponse?.credential;
      if (!credential) {
        setError("Google credential was not returned");
        return;
      }
      setLoading(true);

      const data = await loginWithGoogle(credential);
      if (data?.requiresPasswordSetup) {
        setSetupToken(String(data.setupToken || ""));
        setEmail(String(data?.user?.email || "").toLowerCase());
        setPassword("");
        setConfirmPassword("");
        setView(VIEWS.SETUP);
        setNotice("Set your password once for this Google account.");
        return;
      }

      applyLogin(data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Google login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitEmailLogin = async (event) => {
    event.preventDefault();
    if (loading) return;
    resetMessages();
    if (!normalizedEmail || !password) {
      setError("Email and password are required.");
      return;
    }
    try {
      setLoading(true);
      const data = await loginWithEmail(normalizedEmail, password);
      applyLogin(data);
    } catch (requestError) {
      const code = requestError?.response?.data?.code;
      if (code === "PASSWORD_SETUP_REQUIRED") {
        setNotice(requestError?.response?.data?.message || "Password setup is required first.");
        setView(VIEWS.FORGOT_REQUEST);
        return;
      }
      setError(requestError?.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitSetupPassword = async (event) => {
    event.preventDefault();
    if (loading) return;
    resetMessages();
    if (!setupToken) {
      setError("Setup token missing. Sign in with Google again.");
      return;
    }
    if (!password || !confirmPassword) {
      setError("Please enter and confirm your password.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      setLoading(true);
      const data = await setupPassword(setupToken, password);
      applyLogin(data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to set password");
    } finally {
      setLoading(false);
    }
  };

  const submitForgotRequest = async (event) => {
    event.preventDefault();
    if (loading) return;
    resetMessages();
    if (!normalizedEmail) {
      setError("Enter your email first.");
      return;
    }
    try {
      setLoading(true);
      await requestPasswordResetOtp(normalizedEmail);
      setOtp("");
      setResetToken("");
      setView(VIEWS.FORGOT_VERIFY);
      setNotice("Verification code sent. Check your email.");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const submitOtpVerify = async (event) => {
    event.preventDefault();
    if (loading) return;
    resetMessages();
    if (!normalizedEmail || !otp.trim()) {
      setError("Email and verification code are required.");
      return;
    }
    try {
      setLoading(true);
      const data = await verifyPasswordResetOtp(normalizedEmail, otp.trim());
      setResetToken(String(data?.resetToken || ""));
      setPassword("");
      setConfirmPassword("");
      setView(VIEWS.FORGOT_RESET);
      setNotice("Verification successful. Set your new password.");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const submitPasswordReset = async (event) => {
    event.preventDefault();
    if (loading) return;
    resetMessages();
    if (!resetToken) {
      setError("Reset session expired. Start again.");
      setView(VIEWS.FORGOT_REQUEST);
      return;
    }
    if (!password || !confirmPassword) {
      setError("Please enter and confirm your new password.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      setLoading(true);
      await resetPassword(normalizedEmail, resetToken, password);
      setPassword("");
      setConfirmPassword("");
      setOtp("");
      setResetToken("");
      setSetupToken("");
      setView(VIEWS.LOGIN);
      setNotice("Password updated. You can now login with email and password.");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to reset password");
    } finally {
      setLoading(false);
    }
  };

  const switchView = (nextView) => {
    resetMessages();
    setView(nextView);
  };

  const renderLogin = () => (
    <>
      <form className="space-y-4" onSubmit={submitEmailLogin}>
        <label className="block text-sm font-medium text-slate-700">
          Email
          <input
            type="email"
            className={`${inputField} mt-1`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            placeholder="name@gmail.com"
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Password
          <input
            type="password"
            className={`${inputField} mt-1`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="Enter your password"
          />
        </label>
        <button type="submit" className={`${btnPrimary} w-full`} disabled={loading}>
          {loading ? "Signing in..." : "Login with Email"}
        </button>
      </form>
      <div className="mt-3 text-right">
        <button
          type="button"
          className="text-sm font-medium text-rose-600 underline-offset-2 hover:underline"
          onClick={() => switchView(VIEWS.FORGOT_REQUEST)}
        >
          Forgot Password?
        </button>
      </div>
      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200" />
        <span>OR</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <div className="flex justify-center">
        <GoogleLogin onSuccess={onGoogleSuccess} onError={() => setError("Google sign in failed")} />
      </div>
    </>
  );

  const renderSetup = () => (
    <form className="space-y-4" onSubmit={submitSetupPassword}>
      <label className="block text-sm font-medium text-slate-700">
        Email
        <input type="email" className={`${inputField} mt-1 bg-slate-100`} value={normalizedEmail} readOnly />
      </label>
      <label className="block text-sm font-medium text-slate-700">
        New Password
        <input
          type="password"
          className={`${inputField} mt-1`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="Minimum 8 chars with letters and numbers"
        />
      </label>
      <label className="block text-sm font-medium text-slate-700">
        Confirm Password
        <input
          type="password"
          className={`${inputField} mt-1`}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="Re-enter password"
        />
      </label>
      <button type="submit" className={`${btnPrimary} w-full`} disabled={loading}>
        {loading ? "Saving..." : "Set Password"}
      </button>
      <button type="button" className={`${btnSecondary} w-full`} onClick={() => switchView(VIEWS.LOGIN)}>
        Back to Login
      </button>
    </form>
  );

  const renderForgotRequest = () => (
    <form className="space-y-4" onSubmit={submitForgotRequest}>
      <label className="block text-sm font-medium text-slate-700">
        Gmail
        <input
          type="email"
          className={`${inputField} mt-1`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          placeholder="name@gmail.com"
        />
      </label>
      <button type="submit" className={`${btnPrimary} w-full`} disabled={loading}>
        {loading ? "Sending..." : "Send Verification Code"}
      </button>
      <button type="button" className={`${btnSecondary} w-full`} onClick={() => switchView(VIEWS.LOGIN)}>
        Back to Login
      </button>
    </form>
  );

  const renderForgotVerify = () => (
    <form className="space-y-4" onSubmit={submitOtpVerify}>
      <label className="block text-sm font-medium text-slate-700">
        Gmail
        <input
          type="email"
          className={`${inputField} mt-1`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          placeholder="name@gmail.com"
        />
      </label>
      <label className="block text-sm font-medium text-slate-700">
        Verification Code
        <input
          type="text"
          inputMode="numeric"
          className={`${inputField} mt-1`}
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="Enter 6-digit code"
        />
      </label>
      <button type="submit" className={`${btnPrimary} w-full`} disabled={loading}>
        {loading ? "Verifying..." : "Verify Code"}
      </button>
      <button type="button" className={`${btnSecondary} w-full`} onClick={() => switchView(VIEWS.FORGOT_REQUEST)}>
        Resend Code
      </button>
      <button type="button" className={`${btnSecondary} w-full`} onClick={() => switchView(VIEWS.LOGIN)}>
        Back to Login
      </button>
    </form>
  );

  const renderForgotReset = () => (
    <form className="space-y-4" onSubmit={submitPasswordReset}>
      <label className="block text-sm font-medium text-slate-700">
        New Password
        <input
          type="password"
          className={`${inputField} mt-1`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="Minimum 8 chars with letters and numbers"
        />
      </label>
      <label className="block text-sm font-medium text-slate-700">
        Confirm Password
        <input
          type="password"
          className={`${inputField} mt-1`}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="Re-enter password"
        />
      </label>
      <button type="submit" className={`${btnPrimary} w-full`} disabled={loading}>
        {loading ? "Updating..." : "Reset Password"}
      </button>
      <button type="button" className={`${btnSecondary} w-full`} onClick={() => switchView(VIEWS.LOGIN)}>
        Back to Login
      </button>
    </form>
  );

  const titleByView = {
    [VIEWS.LOGIN]: "Admin Login",
    [VIEWS.SETUP]: "Set Password",
    [VIEWS.FORGOT_REQUEST]: "Forgot Password",
    [VIEWS.FORGOT_VERIFY]: "Verify Code",
    [VIEWS.FORGOT_RESET]: "Reset Password",
  };

  const subtitleByView = {
    [VIEWS.LOGIN]: "Admin can sign in with Google or email + password.",
    [VIEWS.SETUP]: "Complete one-time setup for your Google account.",
    [VIEWS.FORGOT_REQUEST]: "Enter your Gmail to receive a verification code.",
    [VIEWS.FORGOT_VERIFY]: "Enter the verification code sent to your email.",
    [VIEWS.FORGOT_RESET]: "Set a new password for your account.",
  };

  const renderView = () => {
    if (view === VIEWS.SETUP) return renderSetup();
    if (view === VIEWS.FORGOT_REQUEST) return renderForgotRequest();
    if (view === VIEWS.FORGOT_VERIFY) return renderForgotVerify();
    if (view === VIEWS.FORGOT_RESET) return renderForgotReset();
    return renderLogin();
  };

  return (
    <div className={pageShell}>
      <div className={`${pageContainer} max-w-md`}>
        <div className={`${cardGlass} ring-1 ring-slate-100`}>
          <h1 className={pageTitle}>{titleByView[view]}</h1>
          <p className="mb-8 text-center text-sm text-slate-600">{subtitleByView[view]}</p>
          {renderView()}
          {notice ? (
            <p className="mt-6 rounded-xl bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-700 ring-1 ring-emerald-100">
              {notice}
            </p>
          ) : null}
          {error ? (
            <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-center text-sm text-red-700 ring-1 ring-red-100">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
